import { useState, useEffect, useRef } from "react";
import { usePreLessonQuestionsStore } from "@/hooks/use-pre-lesson-questions-store";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import TeacherChatInterface from "./teacher/teacher-chat-interface";
import type { BuildQuestionValues } from "@live-writing/models";
import TeacherService from "@/lib/teacher-service";

interface RightPanelProps {
  preLessonId: number;
  questions?: BuildQuestionValues[];
}

function RightPanel({
  preLessonId,
  questions: initialQuestions = [],
}: RightPanelProps) {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] =
    useState<BuildQuestionValues[]>(initialQuestions);
  const { startClarify, activeClarifyQuestion, clarifyMessages } = usePreLessonQuestionsStore();
  const chatWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeClarifyQuestion && chatWrapperRef.current) {
      chatWrapperRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeClarifyQuestion, clarifyMessages.length]);

  useEffect(() => {
    setQuestions(initialQuestions);
  }, [initialQuestions]);

  const buildFromSubmissions = async () => {
    setLoading(true);
    try {
      const { response_id } = await TeacherService.startGenerateQuestionsJob(preLessonId);
      const poll = async () => {
        try {
          const result = await TeacherService.pollGenerateQuestionsJob(response_id, preLessonId);
          if (result.status === "completed") {
            const transformedQuestions = (result.questions || []).map((q: { question: string; points: string[] }) => ({
              pre_lesson_overview_id: preLessonId,
              question: q.question,
              point1: q.points[0],
              point2: q.points[1],
              point3: q.points[2],
            }));
            setQuestions(transformedQuestions);
            setLoading(false);
          } else if (result.status === "error") {
            console.error("Generate questions job error:", result.error);
            setLoading(false);
          } else {
            setTimeout(poll, 1000);
          }
        } catch (err) {
          console.error("Failed to poll generate questions job:", err);
          setLoading(false);
        }
      };

      setTimeout(poll, 1000);
    } catch (error) {
      console.error("Failed to start generate questions job:", error);
      setLoading(false);
    }
  };

  const openChat = (q: BuildQuestionValues) => startClarify(preLessonId, q);

  return (
    <div className="w-full">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm w-full">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Questions to be Ready For
          </h3>
          <Button
            onClick={buildFromSubmissions}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            disabled={loading}
          >
            {loading ? "Building..." : "Build from student submissions"}
          </Button>
        </div>

        <div className="mt-6">
          {questions.length === 0 ? (
            <p className="text-sm text-gray-500">No questions generated yet</p>
          ) : (
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="border border-gray-100 p-3 rounded">
                  <p className="font-medium text-sm text-gray-900 mb-1">
                    <span className="font-semibold mr-2">{i + 1}.</span>
                    {q.question}
                  </p>
                  <ul className="text-sm text-gray-700 mb-2 list-disc pl-6">
                    {q.point1 && <li className="ml-2">{q.point1}</li>}
                    {q.point2 && <li className="ml-2">{q.point2}</li>}
                    {q.point3 && <li className="ml-2">{q.point3}</li>}
                  </ul>
                  <div className="flex justify-center">
                    <Button
                      onClick={() => openChat(q)}
                      className="text-sm text-white bg-blue-500 border border-gray-200 hover:bg-blue-700 flex items-center gap-2 px-3 py-1 rounded"
                    >
                      <MessageSquare />
                      Teaching ideas
                    </Button>
                  </div>

                  {activeClarifyQuestion &&
                    activeClarifyQuestion.question === q.question && (
                      <div className="mt-4" ref={chatWrapperRef}>
                        <TeacherChatInterface preLessonId={preLessonId} />
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RightPanel;
